<script lang="ts">
import { QRCode } from '@/services/qrcode'
import { Share } from '@/services/share'
import { useBenefitsStore } from '@/stores/benefits'
import type { Benefit, ClaimedCode, Nullable } from '@/types'
import { snakeCase } from 'change-case'
import { createTooltip, destroyTooltip } from 'floating-vue'
import { type DateTime } from 'luxon'
import { type PropType, defineComponent } from 'vue'

type NullableInputElement = Nullable<
  EventTarget & { parentElement: HTMLElement }
>

/**
 * A modal to redeem Greggs.
 */
export default defineComponent({
  props: {
    /**
     * A configured benefit object.
     */
    benefit: {
      type: Object as PropType<Benefit>,
      required: true,
    },
    /**
     * An array of previously claimed codes by the user.
     */
    claimedCodes: {
      type: Object as PropType<Nullable<Array<ClaimedCode>>>,
      required: false,
    },
    /**
     * Disables the redemption button.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      code: null as Nullable<string>,
      expiryDatetime: null as Nullable<DateTime>,
      previouslyRedeemedDatetime: null as Nullable<DateTime>,
      isLoading: false,
      showQRCode: false,
    }
  },
  computed: {
    modalId(): string {
      return `redeemption-modal-${snakeCase(this.benefit.title)}`
    },
  },
  methods: {
    resetState() {
      this.showQRCode = false
    },
    async fetchCode() {
      try {
        this.isLoading = true
        let codes = null

        if (this.claimedCodes?.length) {
          codes = this.claimedCodes
          this.previouslyRedeemedDatetime = this.claimedCodes?.[0].claimed_at
        } else {
          const benefitsStore = useBenefitsStore()
          codes = await benefitsStore.redeem(
            this.benefit.redemption_category,
            this.benefit.redemption_subcategory
          )
        }

        this.expiryDatetime = codes?.[0].expires_at
        this.code = codes?.[0].code

        QRCode.createQRCode({
          code: this.code,
          qrCodeElement: document.getElementById('qrcode') as HTMLElement,
        })

        this.showQRCode = true
      } catch (e: any) {
        this.$alertStore.showErrorAlert({ message: e.message })
      } finally {
        this.isLoading = false
      }
    },
    showQRCodeIfPreviouslyRedeemed() {
      if (this.claimedCodes?.length) {
        this.fetchCode()
      }
    },
    async share() {
      await Share.share({
        title: `Greggs Deals - ${this.BUSINESS_NAME}`,
        text: 'Enjoy your breakfast from the Goodlife!',
        url: 'https://goodlifeplus.co.uk',
        imageElement: document.getElementById('qrcode') as HTMLImageElement,
        imageFilename: 'greggs-qr-code.jpeg',
      })
    },
    async copy(code: string, input: NullableInputElement) {
      let tooltipText: string = 'Copied!'

      try {
        await navigator.clipboard.writeText(code)
      } catch (e: any) {
        tooltipText = e.message
      }

      if (!input) {
        return
      }

      const tooltip = createTooltip(
        input,
        {
          triggers: [],
          content: tooltipText,
          container: input.parentElement,
        },
        {}
      )

      tooltip.show()

      setTimeout(() => {
        tooltip.hide()
        // Transition
        setTimeout(() => {
          destroyTooltip(input)
        }, 500)
      }, 1000)
    },
  },
})
</script>

<template>
  <AppModal
    :id="modalId"
    close-button-class="right-1 top-[1px] text-pure-white"
    class="min-h-[400px] w-[375px]"
    @close="resetState"
    @open="showQRCodeIfPreviouslyRedeemed"
  >
    <template #button="{ openModal }">
      <slot name="button" :open-modal>
        <AppButton
          class="button-secondary button-secondary-light mt-auto min-w-full font-semibold"
          :title="
            disabled
              ? 'This benefit category has already been redeemed'
              : 'Click to claim this benefit'
          "
          :tooltip="{
            content: disabled
              ? 'This benefit category has already been redeemed'
              : 'Click to claim this benefit',
            placement: 'bottom',
            popperClass: 'tertiary',
          }"
          :disabled
          @click="openModal"
        >
          {{ claimedCodes ? 'Claimed' : 'Claim' }}
        </AppButton>
      </slot>
    </template>

    <template #default>
      <div class="flex w-full flex-col">
        <img
          v-if="!showQRCode"
          :src="benefit.image"
          :title="benefit.title"
          class="aspect-square object-cover"
        />

        <div class="flex w-full flex-col">
          <div
            class="bg-primary py-2 text-center text-sm font-medium text-white"
          >
            Renews every 30 days
          </div>

          <div class="flex h-full flex-col gap-6 p-4 pb-8 md:p-5">
            <div class="flex flex-col gap-3">
              <img
                v-if="benefit.brand_image"
                :src="benefit.brand_image"
                :title="`${benefit.title} Logo`"
                class="max-h-6 w-fit object-contain"
              />

              <div>
                <div class="text-xl font-bold md:text-left">
                  Free breakfast baguette deal
                </div>

                <p v-if="!isLoading && previouslyRedeemedDatetime" class="mt-3">
                  Claimed:
                  <strong>{{
                    $d(previouslyRedeemedDatetime.toJSDate(), 'long')
                  }}</strong>
                </p>
              </div>

              <p>
                *Most Greggs shops accept the codes, some exclusions apply.
                Click here for full Terms & Conditions

                <RouterLink
                  :to="{ name: 'breakfast-baguette-deal-terms' }"
                  class="block text-xs underline hover:no-underline"
                  target="_blank"
                >
                  Terms & Conditions
                </RouterLink>
              </p>
            </div>

            <div
              v-show="showQRCode"
              class="flex flex-col items-center justify-center gap-6 rounded-xl bg-black p-6 text-white"
            >
              <div id="qrcode" />
              <div v-if="expiryDatetime" class="text-2xl">
                Valid until {{ $d(expiryDatetime.toJSDate(), 'long') }}
              </div>
            </div>

            <AppButton
              v-if="!showQRCode"
              class="button-primary mt-auto"
              title="Claim my Greggs"
              @click="fetchCode"
              :is-loading="isLoading"
            >
              Claim my Greggs
            </AppButton>
            <AppButton
              v-if="showQRCode"
              class="mt-auto bg-primary"
              title="Share"
              @click="share"
            >
              Share
            </AppButton>
          </div>
        </div>
      </div>
    </template>
  </AppModal>
</template>

<style lang="scss" scoped>
.form-input {
  @apply pr-11;

  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='17' height='20' viewBox='0 0 17 20' fill='none'%3E%3Cpath d='M6 16C5.45 16 4.97917 15.8042 4.5875 15.4125C4.19583 15.0208 4 14.55 4 14V2C4 1.45 4.19583 0.979167 4.5875 0.5875C4.97917 0.195833 5.45 0 6 0H15C15.55 0 16.0208 0.195833 16.4125 0.5875C16.8042 0.979167 17 1.45 17 2V14C17 14.55 16.8042 15.0208 16.4125 15.4125C16.0208 15.8042 15.55 16 15 16H6ZM6 14H15V2H6V14ZM2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4H2V18H13V20H2Z' fill='%231E293B'/%3E%3C/svg%3E")
    no-repeat right 1rem center;
}
</style>
